import React from 'react';
import PropTypes from 'prop-types';
import AmPieChart from '../../../../commonComponents/charts/AmPieChart';
import AmColumnAndLineChart from '../../../../commonComponents/charts/AmColumnAndLineChart';
import SurveyQuestionProp from '../../../survey/propTypes/surveyQuestionProp';
import { useModal } from '../../../../services/customHooks';
import ShowAnswersModal from '../ShowAnswersModal';
import { formatDate } from '../../../../services/utilityFunctions';

const RenderQuestionsPoll = ({ t, questionStats, surveyContext }) => {
  const [showAnswers, setShowAnswersModal] = useModal({ show: false, data: null });
  const { question, stats } = questionStats;
  const { type } = question;
  // console.log('*RenderQuestionsPoll', questionStats, question, stats, type);
  const addEventListener = event => {
    const { answerId, questionId } = event.target.dataItem.dataContext || {};
    setShowAnswersModal({
      show: true,
      data: {
        answerId,
        questionId,
        type,
      },
    });
  };
  /** RENDER CHART */
  const renderChart = typeOfChart => {
    let options;
    let answers = [];
    let data = [];
    switch (typeOfChart) {
      case 'choice': // BRAVON
        answers = stats.answers || [];
        options = Object.keys(question.choices).map(id => ({ _id: id, option: question.choices[id] }));
        data = options.map(o => ({
          answerId: o._id,
          answer: o.option,
          value: answers[o._id] ? answers[o._id].total : 0,
          questionId: question._id,
          type,
        }));
        return (
          <AmPieChart
            chartId="choiceChart"
            containerClass="report-chart-content"
            stats={data}
            options={{
              value: 'value',
              category: 'answer',
              addEventListener,
            }}
          />
        );
      case 'evaluation': // BRAVON
      case 'rating': // SurveyJS
        options = [];
        answers = stats?.evaluations || [];
        // eslint-disable-next-line no-plusplus
        for (let i = 1; i <= question.evaluationMax; i++) {
          options.push({ _id: i.toString(), option: i });
        }
        data = options.map(o => {
          if (answers[o._id] && answers[o._id].total > 0) {
            return ({
              answerId: o._id,
              value: answers[o._id] ? answers[o._id].total : 0,
              answer: o.option,
              questionId: question._id,
              type,
            });
          }
          return {};
        });
        return (
          <div className="">
            <div className="col-12">
            <span className="evaluation-info">
              {t('survey.graph.type.evaluation.content', stats.sum || 0, stats?.average?.toFixed(2) || 0)}
            </span>
            </div>
            <div className="col-12">
              <AmColumnAndLineChart
                containerClass="report-chart-content"
                options={{
                  category: 'answer',
                  yLabel: '', // Y TEXT,
                  columnValueKey: 'value',
                  columnSeriesName: t('survey.graph.type.evaluation'),
                  isRotated: true,
                  valueAxisLimits: { min: 0 },
                  addEventListener,
                }}
                chartId="evaluation"
                stats={data}
              />
            </div>
          </div>
        );
      case 'options':
      case 'multiple_answers':
        options = question.options || [];
        answers = stats.answers || [];
        data = options.map(o => ({
          answerId: o._id,
          value: answers[o._id] ? answers[o._id].total : 0,
          answer: o.option,
          questionId: question._id,
          type,
        }));
        return (
          <AmColumnAndLineChart
            containerClass="report-chart-content"
            options={{
              category: 'answer',
              yLabel: '', // Y TEXT,
              columnValueKey: 'value',
              columnSeriesName: t('survey.graph.generic'),
              valueAxisLimits: { min: 0 },
              addEventListener,
            }}
            chartId={`chartId${type}`}
            stats={data}
          />
        );
      case 'raking':
        options = question.choices || [];
        answers = stats.answers || [];
        data = options.map(o => {
          const rakingAnswers = answers.map(answer => answer.reduce((a, b) => `${a}>${b}`));
          const finalAnswers = rakingAnswers.reduce((prev, cur) => {
            const previous = prev;
            previous[cur] = (prev[cur] || 0) + 1;
            return previous;
          }, {});
          // Count how many times the answers was chosen
          return ({
            answerId: o,
            value: finalAnswers[o] ? finalAnswers[o] : 0,
            answer: o,
          });
        });
        return (
          <AmColumnAndLineChart
            containerClass="report-chart-content"
            options={{
              category: 'answer',
              yLabel: '', // Y TEXT,
              columnValueKey: 'value',
              columnSeriesName: t('survey.graph.generic'),
              isRotated: true,
              valueAxisLimits: { min: 0 },
            }}
            chartId="surveyJs"
            stats={data}
          />
        );
      default: // checkbox, radiogroup, dropdown (SurveyJS)
        options = question.choices || [];
        answers = stats.answers || [];
        data = options.map(o => {
          // Extract the arrays inside of array
          const answersFlat = stats.answers.flat();
          // Count how many times the answers was chosen
          const finalAnswers = answersFlat.reduce((prev, cur) => {
            const previous = prev;
            previous[cur] = (prev[cur] || 0) + 1;
            return previous;
          }, {});
          return ({
            answerId: o,
            value: finalAnswers[o] ? finalAnswers[o] : 0,
            answer: o,
          });
        });
        return (
          <AmColumnAndLineChart
            containerClass="report-chart-content"
            options={{
              category: 'answer',
              yLabel: '', // Y TEXT,
              columnValueKey: 'value',
              columnSeriesName: t('survey.graph.generic'),
              isRotated: true,
              valueAxisLimits: { min: 0 },
            }}
            chartId="surveyJs"
            stats={data}
          />
        );
    }
  };
  const handleModalClose = () => {
    setShowAnswersModal({ show: false, data: null });
  };
  // Data transform
  const contextName = context => {
    let name = context.description;
    if (context.targetTeam) {
      name = `${t('for')} ${context.targetTeam.name}`;
    }
    if (context.targetUser) {
      name = `${t('for')} ${context.targetUser.firstName} ${context.targetUser.lastName}`;
    }
    name += `: ${formatDate(context.createdAt)}`;
    return name;
  };
  return (
    <>
      {showAnswers.show && (
        <ShowAnswersModal
          t={t}
          onClose={handleModalClose}
          data={showAnswers.data}
          contextName={contextName}
          surveyContext={surveyContext}
        />
      )}
      <div className="col-12 poll-answers">
        {renderChart(type)}
      </div>
    </>
  );
};

RenderQuestionsPoll.propTypes = {
  t: PropTypes.func.isRequired,
  questionStats: PropTypes.shape(SurveyQuestionProp.propType).isRequired,
  surveyContext: PropTypes.string.isRequired,
};

RenderQuestionsPoll.defaultProps = {};

export default RenderQuestionsPoll;
